/**
 * Typed Styling System for Semantic and LLM-Friendly Component Styling
 *
 * This file provides TypeScript types for all styling values used across
 * the application, ensuring type safety and providing autocomplete for
 * gradients, colors, and other style properties.
 */

import type { z } from "zod/v4";
import type { SectionBackgroundSchema } from "@/schemas/section-factory";

/**
 * Section background type — derived from the canonical Zod schema in section-factory.ts
 * This is the single source of truth for all background values.
 */
export type SectionBackground = z.infer<typeof SectionBackgroundSchema>;

/**
 * Gradients that have dark/vibrant backgrounds
 * Used for automatic text color selection
 */
export const DARK_GRADIENTS = new Set<string>([
  "purple-vibrant",
  "coral-vibrant",
  "twilight",
  "purple-hero",
  "primary-vibrant",
  "secondary-vibrant",
]);

/**
 * Semantic colors that have dark backgrounds
 * Used for automatic text color selection
 */
export const DARK_SEMANTIC_COLORS = new Set(["neutral", "primary", "secondary", "accent"]);

/**
 * Check if a gradient has a dark background
 */
export function isDarkGradient(name?: string): boolean {
  return name ? DARK_GRADIENTS.has(name) : false;
}

/**
 * Check if a background value produces a dark background.
 * Accepts either a SectionBackground value or a className string.
 */
export function isDarkBackground(value?: string): boolean {
  if (!value) return false;

  if (DARK_SEMANTIC_COLORS.has(value)) return true;
  if (DARK_GRADIENTS.has(value)) return true;

  if (value.includes("bg-neutral")) return true;

  const newGradientMatch = value.match(/bg-grad-(\w+[-\w]*)/);
  if (newGradientMatch) {
    return isDarkGradient(newGradientMatch[1]);
  }

  return false;
}

/**
 * Shadow sizes for cards and components
 */
export type ShadowSize = "none" | "md" | "lg" | "xl";

/**
 * Border radius sizes
 */
export type RoundedSize = "lg" | "2xl" | "3xl";

/**
 * Padding presets for cards and components
 */
export type PaddingSize = "none" | "sm" | "md" | "lg" | "xl";

/**
 * Border styles
 */
export type BorderStyle = "none" | "default" | "thick";

/**
 * Grid column configurations
 */
export type GridColumns = 1 | 2 | 3 | 4;

/**
 * Section padding sizes. "none" emits no `.ds-section--py-*` class — for a
 * migrated kit-adoption root (ADR-0091) whose own class already owns the
 * vertical rhythm (e.g. guide/kit-home `.section`, ADR-0106).
 */
export type SectionPaddingSize = "sm" | "md" | "lg" | "xl" | "none";

/**
 * Pattern overlay types for sections
 */
export type PatternType = "cross" | "dots" | "diagonal" | "hexagon" | "none";

/**
 * Text alignment options
 */
export type TextAlign = "left" | "center" | "right";

/**
 * Icon sizes for IconCircle component
 */
export type IconSize = "xs" | "sm" | "md" | "lg" | "xl";

/**
 * Icon/Component color variants
 */
export type ColorVariant =
  | "primary"
  | "secondary"
  | "accent"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutral";

/**
 * Helper type for polymorphic components that accept an 'as' prop
 */
export type PolymorphicAs = React.ElementType;

/**
 * Base card styling props for card components
 */
export interface BaseCardStylingProps {
  background?: SectionBackground;
  shadow?: ShadowSize;
  rounded?: RoundedSize;
  padding?: PaddingSize;
}
